import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Input, notification, Progress, Tag, Popconfirm, Spin, Empty, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, ClockCircleOutlined, PictureOutlined, VideoCameraOutlined, PlayCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { TextArea } = Input;

const VIDEO_MAX_MB   = 50;
const VIDEO_MAX_SEC  = 60;
const IMAGE_MAX_MB   = 10;

function auth() { return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }; }

function timeLeft(expiresAt) {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

function pct(createdAt, expiresAt) {
  const total   = new Date(expiresAt) - new Date(createdAt);
  const elapsed = new Date() - new Date(createdAt);
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

export default function AdminStatusList() {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const [form,       setForm]       = useState({ title: '', caption: '' });
  const [preview,    setPreview]    = useState(null);   // blob URL
  const [file,       setFile]       = useState(null);
  const [mediaType,  setMediaType]  = useState('photo'); // 'photo' | 'video'
  const [videoDur,   setVideoDur]   = useState(null);   // seconds
  const [durErr,     setDurErr]     = useState('');
  const fileRef  = useRef(null);
  const videoRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/status-photos`, { headers: auth() });
      if (r.data.status) setItems(r.data.result || []);
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearFile = () => {
    setFile(null); setPreview(null); setMediaType('photo');
    setVideoDur(null); setDurErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDurErr('');
    const isVideo = f.type.startsWith('video/');

    if (isVideo && f.size > VIDEO_MAX_MB * 1024 * 1024) {
      setDurErr(`Video ${VIDEO_MAX_MB} MB से बड़ा नहीं होना चाहिए।`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (!isVideo && f.size > IMAGE_MAX_MB * 1024 * 1024) {
      setDurErr(`Image ${IMAGE_MAX_MB} MB से बड़ी नहीं होनी चाहिए।`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setFile(f);
    setMediaType(isVideo ? 'video' : 'photo');
    setPreview(URL.createObjectURL(f));

    if (isVideo) {
      // Read duration via HTML5 video element
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        const dur = Math.round(vid.duration);
        setVideoDur(dur);
        if (dur > VIDEO_MAX_SEC) {
          setDurErr(`Video ${dur} सेकंड का है — अधिकतम ${VIDEO_MAX_SEC} सेकंड (1 मिनट) allowed है।`);
          clearFile();
        }
        URL.revokeObjectURL(vid.src);
      };
      vid.src = URL.createObjectURL(f);
    }
  };

  const handleUpload = async () => {
    if (!file) { notification.warning({ message: 'Photo या Video select करें', placement: 'topRight' }); return; }
    if (durErr) { notification.error({ message: durErr, placement: 'topRight' }); return; }
    if (mediaType === 'video' && videoDur && videoDur > VIDEO_MAX_SEC) {
      notification.error({ message: `Video ${VIDEO_MAX_SEC} सेकंड से छोटी होनी चाहिए।`, placement: 'topRight' }); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);  // field name 'photo' kept for backward compat
      fd.append('title',   form.title);
      fd.append('caption', form.caption);
      if (mediaType === 'video' && videoDur) fd.append('video_duration_sec', String(videoDur));
      const r = await axios.post(`${baseUrl}/admin/status-photos`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.status) {
        notification.success({ message: `✅ ${mediaType === 'video' ? 'Video' : 'Photo'} upload हो गया! 24 घंटे दिखेगा।`, placement: 'topRight' });
        setForm({ title: '', caption: '' });
        clearFile();
        load();
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Upload failed', placement: 'topRight' });
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/status-photos/${id}`, { headers: auth() });
      notification.success({ message: 'Deleted', placement: 'topRight' });
      setItems(prev => prev.filter(p => p.id !== id));
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const live    = items.filter(p => p.is_live);
  const expired = items.filter(p => !p.is_live);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📸 Admin Status Photos & Videos</h2>
        <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 13 }}>
          Upload poster images or short videos — visible to all members for <strong>24 hours</strong>, then automatically deleted.
        </p>
      </div>

      {/* Upload Card */}
      <Card style={{ marginBottom: 24, borderRadius: 12, border: '1.5px dashed #d1d5db' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>

          {/* Preview area */}
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              width: 160, height: 200, borderRadius: 10, border: '2px dashed #d1d5db',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: '#f9fafb', overflow: 'hidden', flexShrink: 0, position: 'relative',
            }}>
            {preview && mediaType === 'photo' && (
              <img src={preview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            {preview && mediaType === 'video' && (
              <>
                <video ref={videoRef} src={preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                  <PlayCircleOutlined style={{ fontSize: 40, color: '#fff' }} />
                </div>
                {videoDur && (
                  <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 11, borderRadius: 4, padding: '2px 6px' }}>
                    {videoDur}s
                  </div>
                )}
              </>
            )}
            {!preview && (
              <>
                <PictureOutlined style={{ fontSize: 24, color: '#9ca3af', marginBottom: 4 }} />
                <VideoCameraOutlined style={{ fontSize: 24, color: '#9ca3af', marginBottom: 8 }} />
                <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', padding: '0 8px' }}>Click to select<br />photo or video</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileChange} />

          {/* Form */}
          <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input
              placeholder="Title (optional) — e.g. रामलालजी का सहयोग"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              maxLength={200}
            />
            <TextArea
              placeholder="Caption (optional) — कोई सन्देश, जानकारी..."
              value={form.caption}
              onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
              rows={3}
              maxLength={500}
            />

            {durErr && <Alert message={durErr} type="error" showIcon style={{ fontSize: 12 }} />}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={mediaType === 'video' ? <VideoCameraOutlined /> : <PlusOutlined />}
                loading={uploading}
                onClick={handleUpload}
                style={{ background: '#7a0000', borderColor: '#7a0000' }}
              >
                Upload (24 Hours)
              </Button>
              {preview && (
                <Button onClick={clearFile}>Clear</Button>
              )}
            </div>

            <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
              ⏰ <strong>24 घंटे</strong> बाद server disk + database से अपने आप delete होगा।<br />
              📷 Photo: max <strong>{IMAGE_MAX_MB} MB</strong> &nbsp;|&nbsp;
              🎬 Video: max <strong>{VIDEO_MAX_MB} MB</strong>, max <strong>{VIDEO_MAX_SEC} sec</strong> (1 मिनट)
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* Live items */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Live Now ({live.length})
            </div>
            {live.length === 0 && <Empty description="No live items" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              {live.map(p => (
                <Card
                  key={p.id}
                  style={{ borderRadius: 10, overflow: 'hidden', border: `1.5px solid ${p.media_type === 'video' ? '#93c5fd' : '#86efac'}` }}
                  bodyStyle={{ padding: 10 }}
                  cover={
                    <div style={{ position: 'relative' }}>
                      {p.media_type === 'video'
                        ? (
                          <div style={{ width: '100%', height: 180, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6 }}>
                            <PlayCircleOutlined style={{ fontSize: 40, color: '#93c5fd' }} />
                            {p.video_duration_sec && <span style={{ color: '#94a3b8', fontSize: 11 }}>{p.video_duration_sec}s</span>}
                          </div>
                        ) : (
                          <img src={p.photo_url} alt={p.title || ''} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        )
                      }
                      <Tag color={p.media_type === 'video' ? 'blue' : 'green'} style={{ position: 'absolute', top: 6, left: 6, fontSize: 10 }}>
                        {p.media_type === 'video' ? <><VideoCameraOutlined /> Video</> : <><ClockCircleOutlined /> Photo</>}
                      </Tag>
                      <Tag color="default" style={{ position: 'absolute', top: 6, right: 6, fontSize: 10 }}>
                        <ClockCircleOutlined /> {timeLeft(p.expires_at)}
                      </Tag>
                    </div>
                  }
                >
                  {p.title && <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>}
                  {p.caption && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.caption}</div>}
                  <Progress percent={pct(p.created_at, p.expires_at)} size="small" strokeColor="#ef4444" showInfo={false} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}><EyeOutlined /> {p.view_count || 0} views</span>
                    <Popconfirm title="Delete from server?" onConfirm={() => handleDelete(p.id)} okText="Delete" okButtonProps={{ danger: true }}>
                      <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Expired */}
          {expired.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#9ca3af', marginBottom: 10 }}>
                Expired ({expired.length}) — already deleted from server
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                {expired.map(p => (
                  <Card key={p.id} style={{ borderRadius: 10, opacity: 0.6, border: '1px solid #e5e7eb' }} bodyStyle={{ padding: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 2 }}>{p.title || 'Untitled'}</div>
                    <Tag color={p.media_type === 'video' ? 'blue' : 'default'} style={{ fontSize: 10 }}>
                      {p.media_type === 'video' ? 'Video' : 'Photo'} — Expired
                    </Tag>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{p.view_count || 0} views total</div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
